import Box from '@mui/material/Box';
import { useEffect, useRef, useState } from 'react';
import type { ChangeEvent, ReactElement } from 'react';

import { tokens } from '../../../design/tokens';

/**
 * CodeInput — single hidden TextField drives N visible cells.
 *
 * Pattern ported from `packages/mobile/src/ui/OTPInput.tsx` to the web.
 * Why one hidden input, not N visible inputs:
 *
 *   - **iOS Safari + Android Chrome SMS autofill** deliver the full code
 *     to a single field via `autoComplete="one-time-code"` — multi-input
 *     setups silently drop the autofill.
 *   - **Paste handling** is automatic when the field accepts the whole
 *     string at once; multi-input pasting requires hand-rolled splitting.
 *   - **Auto-submit** fires from a single `onComplete` callback once the
 *     value reaches `length` digits.
 *
 * The visible cells are presentational — they render `value[i] || ''` and
 * a focus indicator when `i === value.length`. Tapping any cell focuses
 * the hidden input.
 *
 * Used by OtpStep and TotpStep (both 6 cells per LOCK 1).
 */
export type CodeInputProps = {
  length?: number;
  value: string;
  onChange: (next: string) => void;
  onComplete?: (value: string) => void;
  disabled?: boolean;
  autoFocus?: boolean;
  errored?: boolean;
  ariaLabel: string;
  testId?: string;
};

type CellProps = {
  char: string;
  filled: boolean;
  isActive: boolean;
  disabled: boolean;
  errored: boolean;
};

const Cell = ({ char, filled, isActive, disabled, errored }: CellProps): ReactElement => (
  <Box
    component="li"
    sx={{
      flex: 1,
      height: 64,
      borderRadius: '14px',
      border: '2px solid',
      borderColor: errored
        ? tokens.state.danger
        : isActive || filled
          ? tokens.brand.primary
          : tokens.border.default,
      background: filled
        ? tokens.brand.subtle
        : isActive
          ? tokens.surface.base
          : tokens.surface.elevated,
      color: filled ? tokens.brand.primary : tokens.text.primary,
      fontFamily: tokens.font.mono,
      fontSize: 26,
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 150ms',
      boxShadow: isActive
        ? '0 0 0 4px rgba(15, 107, 58, 0.14), 0 6px 16px rgba(15, 107, 58, 0.12)'
        : 'none',
      cursor: disabled ? 'not-allowed' : 'text',
      opacity: disabled ? 0.6 : 1,
    }}
  >
    {char}
  </Box>
);

type CodeInputEffectsArgs = {
  inputRef: React.RefObject<HTMLInputElement | null>;
  value: string;
  length: number;
  disabled: boolean;
  errored: boolean;
  onComplete: ((value: string) => void) | undefined;
};

function useCodeInputEffects({
  inputRef,
  value,
  length,
  disabled,
  errored,
  onComplete,
}: CodeInputEffectsArgs): void {
  useEffect(() => {
    if (value.length === length && /^\d+$/.test(value) && onComplete && !disabled) {
      onComplete(value);
    }
    // onComplete intentionally excluded — caller may rebind every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, length, disabled]);

  // Re-focus on error so the admin doesn't have to re-click cells after a typo.
  useEffect(() => {
    if (errored && !disabled) inputRef.current?.focus();
  }, [inputRef, errored, disabled]);
}

export const CodeInput = ({
  length = 6,
  value,
  onChange,
  onComplete,
  disabled = false,
  autoFocus = false,
  errored = false,
  ariaLabel,
  testId,
}: CodeInputProps): ReactElement => {
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  useCodeInputEffects({ inputRef, value, length, disabled, errored, onComplete });

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const digits = event.target.value.replace(/\D/g, '').slice(0, length);
    onChange(digits);
  };

  const focusInput = (): void => inputRef.current?.focus();
  const activeIndex = Math.min(value.length, length - 1);

  return (
    <Box sx={{ position: 'relative', width: '100%' }} data-testid={testId}>
      <Box
        component="ul"
        onClick={focusInput}
        aria-hidden="true"
        sx={{ display: 'flex', gap: '12px', listStyle: 'none', padding: 0, margin: 0 }}
      >
        {Array.from({ length }).map((_, i) => (
          <Cell
            key={i}
            char={value[i] ?? ''}
            filled={i < value.length}
            isActive={focused && i === activeIndex}
            disabled={disabled}
            errored={errored}
          />
        ))}
      </Box>
      <Box
        component="input"
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus={autoFocus}
        disabled={disabled}
        maxLength={length}
        value={value}
        onChange={handleChange}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        aria-label={ariaLabel}
        data-testid={testId ? `${testId}-input` : 'code-input-hidden'}
        sx={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          opacity: 0,
          border: 0,
          outline: 0,
          padding: 0,
          margin: 0,
          background: 'transparent',
          color: 'transparent',
          caretColor: 'transparent',
          letterSpacing: '24px',
          fontSize: 26,
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'text',
        }}
      />
    </Box>
  );
};
